<script>
import aboutPhotoUrl from '@src/assets/images/bowvue_home_page_1.webp'
import { authComputed } from '@state/helpers'
import {
  logoComputed,
  dashboardComputed,
  bowtieLogoComputed,
} from '@utils/themed-image'
import appConfig from '@src/app.config'
import Layout from '@layouts/main'
// Ask bar parked. The floating BowVue AI launcher is the single entry point
// again. Restore this import, the registration below and the markup in the
// template together, and drop `hideChatLauncher` back into this page's route.
// import AskBar from '@components/ask-bar'
import {
  mdiQrcodeScan,
  mdiFileDocumentMultipleOutline,
  mdiShieldCheckOutline,
  mdiCashMultiple,
} from '@mdi/js'

export default {
  metaInfo: {
    // Rendered as "<title> | BowVue". Keep it category-explicit; this is
    // the headline search and answer engines show for the site.
    title: 'Arc Flash & Electrical Safety Compliance Software',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: { Layout /*, AskBar */ },
  data() {
    return {
      // Icon + i18n key per card; titles/text resolve at render time so they
      // follow the active locale (see the template's $t on feature.titleKey).
      features: [
        { icon: mdiQrcodeScan, key: 'qrLabels' },
        { icon: mdiFileDocumentMultipleOutline, key: 'records' },
        { icon: mdiShieldCheckOutline, key: 'compliance' },
        { icon: mdiCashMultiple, key: 'savings' },
      ],
      capabilityKeys: [
        'electricalEngineering',
        'arcFlashAnalysis',
        'preventiveMaintenance',
        'netaTesting',
        'infraredThermography',
        'electricalSafetyTraining',
      ],
    }
  },
  computed: {
    ...logoComputed,
    ...dashboardComputed,
    ...bowtieLogoComputed,
    // Static image imports are MODULE scope, and the Options API does not expose
    // module scope to the template: binding them directly left :src undefined
    // and v-img rendered nothing at all. They have to come through here.
    aboutPhotoSrc() {
      return aboutPhotoUrl
    },
    ...authComputed,
  },
}
</script>

<template>
  <Layout>
    <div class="home-page mx-auto">
      <!-- Ask bar parked; the floating launcher is the only way in again.
           Signed-in visitors only: AskBar renders nothing without entitlement, and
           entitlement is never fetched while signed out. So for the audience this
           marketing page was written for, the hero is still the first thing on it.
      <div class="d-flex justify-center">
        <AskBar class="home-ask-bar" />
      </div>
      -->

      <!-- Hero -->
      <v-row align="center" class="home-hero">
        <v-col cols="12" md="6">
          <v-img
            :src="logoSrc"
            alt="BowVue Risk Management"
            width="320"
            max-width="80%"
            class="mb-8"
          />
          <h1 class="text-h4 text-md-h3 font-weight-bold mb-5 home-hero-title">
            {{ $t('home.heroTitle') }}
          </h1>
          <p class="text-body-1 text-medium-emphasis mb-8 home-hero-subtitle">
            {{ $t('home.heroSubtitle') }}
          </p>
          <div class="d-flex flex-wrap ga-3">
            <v-btn
              v-if="loggedIn"
              color="brand"
              size="large"
              variant="flat"
              prepend-icon="$dashboard"
              :to="{ name: 'dashboard' }"
            >
              {{ $t('home.goToDashboard') }}
            </v-btn>
            <v-btn
              v-else
              color="brand"
              size="large"
              variant="flat"
              :to="{ name: 'login' }"
            >
              {{ $t('common.logIn') }}
            </v-btn>
            <v-btn
              color="brand"
              size="large"
              variant="outlined"
              :to="{ name: 'contact us' }"
            >
              {{ $t('common.contactUs') }}
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="6">
          <v-card variant="flat" border rounded="lg" class="home-hero-shot">
            <!-- A picture of the app, so it follows the theme: a light
                 screenshot sitting in a dark page reads as a bright slab and
                 misrepresents what the product looks like. -->
            <v-img :src="dashboardShotSrc" alt="The BowVue dashboard" cover />
          </v-card>
        </v-col>
      </v-row>

      <!-- Feature highlights -->
      <div class="text-center mt-16 mb-8">
        <h2 class="text-h5 text-md-h4 font-weight-bold">
          {{ $t('home.featuresTitle') }}
        </h2>
        <p class="text-body-1 text-medium-emphasis mt-2">
          {{ $t('home.featuresSubtitle') }}
        </p>
      </div>
      <v-row>
        <v-col
          v-for="feature in features"
          :key="feature.key"
          cols="12"
          sm="6"
          lg="3"
        >
          <v-card variant="flat" border rounded="lg" class="fill-height">
            <v-card-text class="pa-6 pa-lg-8">
              <v-avatar color="brand" variant="tonal" rounded="lg" size="56">
                <v-icon :icon="feature.icon" size="28" />
              </v-avatar>
              <div class="text-h6 font-weight-bold mt-4 mb-2">
                {{ $t(`home.features.${feature.key}.title`) }}
              </div>
              <div class="text-body-2 text-medium-emphasis feature-text">
                {{ $t(`home.features.${feature.key}.text`) }}
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- About Bowtie Engineering -->
      <v-card variant="flat" border rounded="lg" class="mt-16">
        <v-row align="center" no-gutters>
          <v-col cols="12" md="5">
            <v-img
              :src="aboutPhotoSrc"
              alt="Bowtie Engineering technicians at work"
              cover
              class="home-about-image"
            />
          </v-col>
          <v-col cols="12" md="7">
            <div class="pa-8 pa-lg-12 home-about-copy">
              <v-img
                :src="bowtieLogoSrc"
                alt="Bowtie Engineering"
                class="mb-4 home-about-logo"
              />
              <h2 class="text-h5 font-weight-bold mb-3 home-about-title">
                {{ $t('home.aboutTitle') }}
              </h2>
              <!-- The John Welch link sits mid-sentence, so use <i18n-t> with a
                   named slot for {name} rather than splitting the paragraph. -->
              <i18n-t
                keypath="home.aboutParagraph1"
                tag="p"
                scope="global"
                class="text-body-2 text-medium-emphasis mb-3"
              >
                <template v-slot:name>
                  <a
                    href="https://www.linkedin.com/in/john-welch-p-e-cem-60835a8/"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ $t('home.aboutJohnWelch') }}</a
                  >
                </template>
              </i18n-t>
              <p class="text-body-2 text-medium-emphasis mb-4">
                {{ $t('home.aboutParagraph2') }}
              </p>
              <v-chip
                v-for="key in capabilityKeys"
                :key="key"
                variant="tonal"
                color="brand"
                size="small"
                class="mr-2 mb-2"
              >
                {{ $t(`home.capabilities.${key}`) }}
              </v-chip>
            </div>
          </v-col>
        </v-row>
      </v-card>

      <!-- Closing call to action.
           brandSurface, not brand: this is a large brand FILL, and `brand` is
           lightened in the dark theme so it can be read as text. White on that
           lightened red is 3.19:1. brandSurface stays the deep #BF0000 in both
           themes, and its on-token supplies the white, so the hardcoded
           text-white classes are gone too. -->
      <v-card
        color="brandSurface"
        variant="flat"
        rounded="lg"
        class="mt-16 mb-4 text-center"
      >
        <v-card-text class="pa-10 pa-lg-14">
          <h2 class="text-h5 text-md-h4 font-weight-bold mb-3">
            {{ $t('home.ctaTitle') }}
          </h2>
          <p class="text-body-1 mb-8 home-cta-subtitle">
            {{ $t('home.ctaSubtitle') }}
          </p>
          <v-btn
            v-if="loggedIn"
            color="white"
            variant="flat"
            size="large"
            prepend-icon="$dashboard"
            :to="{ name: 'dashboard' }"
          >
            {{ $t('home.openDashboard') }}
          </v-btn>
          <v-btn
            v-else
            color="white"
            variant="flat"
            size="large"
            :to="{ name: 'contact us' }"
          >
            {{ $t('home.getInTouch') }}
          </v-btn>
        </v-card-text>
      </v-card>
    </div>
  </Layout>
</template>

<style scoped>
/* Parked with the ask bar itself.
.home-ask-bar {
  A row of its own, so the component's header-row behaviour does not apply: it
  should not grow to fill, and it has no neighbours to be inset from. The bottom
  margin keeps it off the hero rather than the hero off it. The bar is the thing
  that was added here.
  flex: 0 1 auto;
  width: 100%;
  margin: 0 0 32px;
}
*/

/* 1920px, up from 1440px, at 1440 an xl monitor showed 240px of margin each side
   and a 2560px one showed 560px, which made the landing look boxed in.
   Lower than admin-home's 2400px deliberately: this page is a hero and feature rows
   split into md="6" halves, so at 2400 each half would be 1200px wide wrapping a
   620px paragraph, mostly empty column. The text keeps its own caps
   (.home-hero-subtitle, .feature-text) so widening the page cannot lengthen a line
   of prose past what is comfortable to read. */
.home-page {
  max-width: 1920px;
}

/* Let the hero own most of the first viewport so the landing feels like a
   full page rather than a strip of content, while never collapsing below its
   natural height on short screens. */
.home-hero {
  padding-top: 24px;
  min-height: min(640px, calc(100vh - 260px));
  align-content: center;
}

/* Between Vuetify's h3 (3rem) and h2 (3.75rem) on large screens. Big enough
   to anchor the hero without overpowering it. !important: Vuetify's text-*
   typography utilities are themselves !important. */
@media (min-width: 1280px) {
  .home-hero-title {
    font-size: 3.25rem !important;
    line-height: 1.15;
  }
}

.home-hero-subtitle {
  max-width: 620px;
  line-height: 1.7;
}

.feature-text {
  line-height: 1.6;
}

.home-hero-shot {
  overflow: hidden;
}

.home-about-image {
  height: 100%;
  min-height: 260px;
}

/* The image is height:100% + cover, so IT dictates the row's height. The copy beside
   it stayed at text-body-2 (14px) in a md="7" column no matter how wide the screen
   got, which left the tall empty box on large monitors. Scaling the copy with the
   viewport lets it meet the image instead of floating in it.
   !important throughout: Vuetify's text-* utilities are !important themselves, and
   the logo's width is an inline style from v-img. Same reason .home-hero-title above
   needs it. */
.home-about-logo {
  width: 220px !important;
}

@media (min-width: 1280px) {
  .home-about-logo {
    width: 260px !important;
  }

  .home-about-title {
    font-size: 2rem !important;
    line-height: 1.2;
  }

  /* 14px -> 16px, with the leading opened to match: a longer line at a larger size
     needs more line-height, not just a bigger font. */
  .home-about-copy :deep(.text-body-2) {
    font-size: 1rem !important;
    line-height: 1.7 !important;
  }
}

@media (min-width: 1920px) {
  .home-about-logo {
    width: 300px !important;
  }

  .home-about-title {
    font-size: 2.25rem !important;
  }

  .home-about-copy :deep(.text-body-2) {
    font-size: 1.0625rem !important;
  }

  /* The capability chips would read as undersized beside 17px copy. */
  .home-about-copy :deep(.v-chip) {
    font-size: 0.875rem !important;
  }
}

.home-cta-subtitle {
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
}
</style>
